import { ReactNode } from "react";

// ── Exported types ────────────────────────────────────────

/** Rendered control ready for consumption by the settings panel. */
export interface SettingsControl {
  group: string;
  control: ReactNode;
}

/** Built-in settings groups shared across widget types. */
export const SettingsGroups = {
  preset: "Preset",
  quick: "Quick Actions"
} as const;

/** Settings groups used by chart-type widgets (MPC, SAM, Chart). */
export const ChartSettingsGroups = {
  title: "Chart Title",
  axis: "Chart Axis",
  legend: "Chart Legend",
  advanced: "Advanced Chart Settings"
} as const;

// ── Control definition types ──────────────────────────────

/**
 * Visibility mode for a screenshot settings control.
 *
 * - `"always"`     — visible in both standalone and dashboard mode
 * - `"standalone"` — visible only when the widget is NOT in dashboard screenshot mode
 * - `(ctx) => boolean` — custom predicate for complex conditions
 */
export type ControlVisibility =
  | "always"
  | "standalone"
  | ((ctx: ControlContext) => boolean);

/** Discriminated input descriptor. */
export type ControlInput =
  | {
      readonly type: "number";
      readonly unit?: string;
      readonly min?: number;
      readonly max?: number;
      readonly step?: number;
    }
  | { readonly type: "boolean" }
  | { readonly type: "text" };

/**
 * Pure data descriptor for a screenshot settings control.
 *
 * Separates *what* a control is (data) from *when* it appears (visibility)
 * and *how* it renders (handled by `buildControls`).
 */
export interface ControlDef {
  readonly name: string;
  readonly label: string;
  readonly group: string;
  readonly input: ControlInput;
  readonly visibility: ControlVisibility;
  readonly valuePropName?: string;
  /** Override the default `<ScreenshotSetting.Input>` with a custom child. */
  readonly renderChild?: () => ReactNode;
}

/** Runtime context passed to visibility predicates. */
export interface ControlContext {
  readonly isDashboard: boolean;
}

// ── FP utilities ──────────────────────────────────────────

/** Evaluate whether a single control should be visible. */
export const isVisible = ({ visibility }: ControlDef, ctx: ControlContext): boolean => {
  if (typeof visibility === "function") return visibility(ctx);
  return visibility === "always" || !ctx.isDashboard;
};

/** Filter an array of control definitions, keeping only those visible in `ctx`. */
export const filterVisible = (
  defs: readonly ControlDef[],
  ctx: ControlContext
): ControlDef[] => defs.filter((d) => isVisible(d, ctx));
